import { ApiError, type StaffApi } from "#api.ts";
import { runEffectQuery } from "#effect-query.ts";
import {
  activeStates,
  mergeOrders,
  ordersPageSchema,
  type Order,
} from "#orders.ts";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { Effect } from "effect";

export const activeOrdersQueryKey = ["orders", "active"] as const;

const getOrdersPage = Effect.fn("StaffApi.getOrdersPage")(
  (api: StaffApi, path: string, signal: AbortSignal) =>
    api.requestEffect({ path, schema: ordersPageSchema, signal }),
);

const listOrdersByState = Effect.fn("OrdersQuery.listByState")(
  (api: StaffApi, status: (typeof activeStates)[number], signal: AbortSignal) =>
    Effect.gen(function* () {
      let orders: readonly Order[] = [];
      let cursor: string | undefined;
      const seen = new Set<string>();

      do {
        const query = new URLSearchParams({
          state: status,
          limit: "100",
          order: "asc",
        });

        if (cursor) query.set("cursor", cursor);
        const page = yield* getOrdersPage(api, `/orders?${query}`, signal);

        orders = mergeOrders(orders, page.orders);
        cursor = page.nextCursor ?? undefined;

        if (cursor && seen.has(cursor))
          return yield* Effect.fail(
            new ApiError(
              502,
              "Order history could not finish loading. Please refresh.",
            ),
          );

        if (cursor) seen.add(cursor);
      } while (cursor);

      return orders;
    }),
);

const listActiveOrders = Effect.fn("OrdersQuery.listActive")(
  (api: StaffApi, signal: AbortSignal) =>
    Effect.gen(function* () {
      const byState = yield* Effect.forEach(
        activeStates,
        (status) => listOrdersByState(api, status, signal),
        { concurrency: "unbounded" },
      );

      let orders: readonly Order[] = [];

      for (const stateOrders of byState) {
        orders = mergeOrders(orders, stateOrders);
      }

      return orders;
    }),
);

const reconcileSnapshot = (
  current: readonly Order[],
  incoming: readonly Order[],
  beforeRefresh: ReadonlyMap<string, string>,
) => {
  const returnedIds = new Set(incoming.map((order) => order.id));

  return mergeOrders(current, incoming).filter(
    (order) =>
      !order.active ||
      returnedIds.has(order.id) ||
      beforeRefresh.get(order.id) !== order.updatedAt,
  );
};

/** Reusable active-order query with Effect cancellation and protocol-owned retries. */
export const activeOrdersQueryOptions = (
  api: StaffApi,
  queryClient: QueryClient,
  enabled: boolean,
) =>
  queryOptions<readonly Order[], ApiError>({
    queryKey: activeOrdersQueryKey,
    enabled,
    retry: false,
    queryFn: ({ signal }) => {
      const before = queryClient.getQueryData<readonly Order[]>(
        activeOrdersQueryKey,
      ) ?? [];

      const beforeRefresh = new Map(
        before
          .filter((order) => order.active)
          .map((order) => [order.id, order.updatedAt]),
      );

      const program = listActiveOrders(api, signal).pipe(
        Effect.map((incoming) =>
          reconcileSnapshot(
            queryClient.getQueryData<readonly Order[]>(activeOrdersQueryKey) ??
              [],
            incoming,
            beforeRefresh,
          ),
        ),
      );

      return runEffectQuery(program, signal);
    },
  });
